import type { QuickSearchResponseRecord } from '@/search/types'
import { normalizeCreatorUrlStub } from '@/lib/blogAuthorPaths'

export type CreatorSuggestion = {
  name: string
  href: string
}

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('en-GB')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const matchesCreatorName = (name: string, query: string) => {
  const normalizedName = normalizeSearchText(name)
  const normalizedQuery = normalizeSearchText(query)

  if (!normalizedName || !normalizedQuery) return false
  if (normalizedName.includes(normalizedQuery)) return true

  const nameTokens = normalizedName.split(' ')
  return normalizedQuery
    .split(' ')
    .every(queryToken =>
      nameTokens.some(nameToken => nameToken.startsWith(queryToken))
    )
}

export function getMatchingCreatorSuggestions(
  results: QuickSearchResponseRecord[],
  query: string,
  limit = 3
): CreatorSuggestion[] {
  if (!query.trim() || limit <= 0) return []

  const suggestions: CreatorSuggestion[] = []
  const seenNames = new Set<string>()

  const addSuggestion = (suggestion: CreatorSuggestion) => {
    const normalizedName = normalizeSearchText(suggestion.name)
    if (!normalizedName || seenNames.has(normalizedName)) return

    seenNames.add(normalizedName)
    suggestions.push(suggestion)
  }

  // Prefer the exact creator URL stubs added by the current indexer.
  results.forEach(result => {
    result.creatorNames?.forEach((name, index) => {
      const slug = normalizeCreatorUrlStub(result.creatorSlugs?.[index])
      const trimmedName = name.trim()

      if (!slug || !matchesCreatorName(trimmedName, query)) return

      addSuggestion({
        name: trimmedName,
        href: `/magician/${slug}/`,
      })
    })
  })

  // Keep autocomplete working while older Typesense documents are still live.
  // The legacy field joins multiple creators with " and ", so match each name
  // independently instead of creating a broken collaboration URL.
  results.forEach(result => {
    if (result.creatorNames?.length) return

    result.creators
      ?.split(/\s+and\s+/i)
      .map(name => name.trim())
      .filter(name => name && name !== 'Unknown')
      .forEach(name => {
        const slug = normalizeCreatorUrlStub(name)
        if (!slug || !matchesCreatorName(name, query)) return

        addSuggestion({
          name,
          href: `/magician/${slug}/`,
        })
      })
  })

  return suggestions.slice(0, limit)
}
